/**
 * Canonical user-visible formatting (Phase 2).
 * Settings: global.date_format, global.timezone, global.currency, global.temperature.
 * Locale: pass active UI locale (sk|en|de); do not invent precedence here.
 *
 * Storage vs display:
 * - Dates/timestamps: transport ISO/SQL; display in configured date_format (+ optional TZ).
 * - Temperature API/DB: Celsius; convert only for display/labels when unit ≠ °C.
 * - Currency/numbers: display only; do not change stored numerics.
 */

import { DateTime } from 'luxon';
import { getCurrency, getDateFormat, getTemperature, getTimezone } from './moduleSettings';

export const EMPTY_DISPLAY = '—';
export const DEFAULT_DATE_FORMAT = 'dd.MM.yyyy';
/** Product time rule: 24h HH:mm (no company setting). */
export const TIME_FORMAT = 'HH:mm';

const LOCALE_TO_BCP47 = {
  sk: 'sk-SK',
  en: 'en-GB',
  de: 'de-DE',
  gb: 'en-GB',
};

/**
 * @param {string|null|undefined} appLocale sk|en|de
 * @returns {string} BCP-47 tag for Intl
 */
export function resolveNumberLocale(appLocale) {
  const key = String(appLocale ?? 'sk').trim().toLowerCase();
  return LOCALE_TO_BCP47[key] || LOCALE_TO_BCP47.sk;
}

function parseDateTime(value, zone) {
  if (value == null || value === '') return null;
  const raw = String(value).trim();
  if (!raw) return null;

  const opts = zone ? { zone } : { setZone: true };
  let dt = DateTime.fromISO(raw, opts);
  if (!dt.isValid) dt = DateTime.fromSQL(raw, opts);
  if (!dt.isValid && raw.includes(' ') && !raw.includes('T')) {
    dt = DateTime.fromISO(raw.replace(' ', 'T'), opts);
  }
  if (!dt.isValid) return null;
  return zone ? dt.setZone(zone) : dt;
}

/**
 * @param {unknown} value
 * @param {{ dateFormat?: string, timezone?: string|null, withTime?: boolean, empty?: string }} [opts]
 */
export function formatDate(value, opts = {}) {
  const {
    dateFormat = DEFAULT_DATE_FORMAT,
    timezone = null,
    withTime = false,
    empty = '',
  } = opts;
  const dt = parseDateTime(value, timezone || undefined);
  if (!dt) return empty;
  const fmt = withTime ? `${dateFormat} ${TIME_FORMAT}` : dateFormat;
  return dt.toFormat(fmt);
}

/**
 * @param {unknown} value
 * @param {{ dateFormat?: string, timezone?: string|null, empty?: string }} [opts]
 */
export function formatDateTime(value, opts = {}) {
  return formatDate(value, { ...opts, withTime: true });
}

/**
 * @param {unknown} value
 * @param {{ timezone?: string|null, empty?: string }} [opts]
 */
export function formatTime(value, opts = {}) {
  const { timezone = null, empty = '' } = opts;
  const dt = parseDateTime(value, timezone || undefined);
  if (!dt) return empty;
  return dt.toFormat(TIME_FORMAT);
}

/**
 * @param {unknown} value
 * @param {{ locale?: string, minimumFractionDigits?: number, maximumFractionDigits?: number, empty?: string }} [opts]
 */
export function formatNumber(value, opts = {}) {
  const {
    locale = 'sk',
    minimumFractionDigits = 0,
    maximumFractionDigits = 2,
    empty = '',
  } = opts;
  if (value == null || value === '') return empty;
  const num = typeof value === 'number' ? value : Number(String(value).replace(',', '.'));
  if (!Number.isFinite(num)) return empty;
  return new Intl.NumberFormat(resolveNumberLocale(locale), {
    minimumFractionDigits,
    maximumFractionDigits,
  }).format(num);
}

/**
 * @param {unknown} value
 * @param {{ currency?: string, locale?: string, empty?: string }} [opts]
 */
export function formatCurrency(value, opts = {}) {
  const { currency = '€', locale = 'sk', empty = '' } = opts;
  if (value == null || value === '') return empty;
  const num = typeof value === 'number' ? value : Number(String(value).replace(',', '.'));
  if (!Number.isFinite(num)) return empty;
  const formatted = formatNumber(num, {
    locale,
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
    empty: '',
  });
  if (!formatted) return empty;
  return `${formatted} ${String(currency || '').trim()}`.trim();
}

/**
 * Percent display when input is already a percent number (25 → "25 %").
 * Does NOT multiply by 100. Use formatPercentRatio for 0.25 → 25%.
 *
 * @param {unknown} value
 * @param {{ locale?: string, maximumFractionDigits?: number, empty?: string }} [opts]
 */
export function formatPercent(value, opts = {}) {
  const { locale = 'sk', maximumFractionDigits = 2, empty = '' } = opts;
  if (value == null || value === '') return empty;
  const num = typeof value === 'number' ? value : Number(String(value).replace(',', '.'));
  if (!Number.isFinite(num)) return empty;
  const formatted = formatNumber(num, {
    locale,
    minimumFractionDigits: 0,
    maximumFractionDigits,
    empty: '',
  });
  if (!formatted) return empty;
  return `${formatted} %`;
}

/**
 * Ratio input: 0.25 → "25 %".
 */
export function formatPercentRatio(value, opts = {}) {
  if (value == null || value === '') return opts.empty ?? '';
  const num = typeof value === 'number' ? value : Number(String(value).replace(',', '.'));
  if (!Number.isFinite(num)) return opts.empty ?? '';
  return formatPercent(num * 100, opts);
}

export function normalizeTemperatureUnit(unit) {
  const u = String(unit ?? '°C').trim();
  if (u === 'K' || u === '°K' || u.toLowerCase() === 'kelvin') return 'K';
  if (u === '°F' || u === 'F' || u.toLowerCase() === 'fahrenheit') return '°F';
  return '°C';
}

/**
 * Convert temperature. Storage/API canonical unit is Celsius.
 * @param {unknown} value
 * @param {string} fromUnit
 * @param {string} toUnit
 * @returns {number|null}
 */
export function convertTemperature(value, fromUnit, toUnit) {
  if (value == null || value === '') return null;
  const num = typeof value === 'number' ? value : Number(String(value).replace(',', '.'));
  if (!Number.isFinite(num)) return null;
  const from = normalizeTemperatureUnit(fromUnit);
  const to = normalizeTemperatureUnit(toUnit);
  if (from === to) return num;

  let celsius = num;
  if (from === '°F') celsius = (num - 32) * (5 / 9);
  else if (from === 'K') celsius = num - 273.15;

  if (to === '°C') return celsius;
  if (to === '°F') return celsius * (9 / 5) + 32;
  if (to === 'K') return celsius + 273.15;
  return celsius;
}

/**
 * Format a Celsius-stored value for display in configured unit.
 * @param {unknown} valueCelsius
 * @param {{ unit?: string, locale?: string, maximumFractionDigits?: number, empty?: string, withUnit?: boolean }} [opts]
 */
export function formatTemperature(valueCelsius, opts = {}) {
  const {
    unit = '°C',
    locale = 'sk',
    maximumFractionDigits = 0,
    empty = '',
    withUnit = true,
  } = opts;
  const converted = convertTemperature(valueCelsius, '°C', unit);
  if (converted == null) return empty;
  const formatted = formatNumber(converted, {
    locale,
    minimumFractionDigits: 0,
    maximumFractionDigits,
    empty: '',
  });
  if (!formatted) return empty;
  const label = normalizeTemperatureUnit(unit);
  return withUnit ? `${formatted} ${label}` : formatted;
}

/**
 * Domain measurement label helper (DOMAIN_FIXED units — no conversion).
 * @param {unknown} value
 * @param {string} unit e.g. mm, kg, kW
 * @param {{ locale?: string, maximumFractionDigits?: number, empty?: string }} [opts]
 */
export function formatMeasurement(value, unit, opts = {}) {
  const { locale = 'sk', maximumFractionDigits = 2, empty = '' } = opts;
  const formatted = formatNumber(value, {
    locale,
    minimumFractionDigits: 0,
    maximumFractionDigits,
    empty: '',
  });
  if (!formatted) return empty;
  const u = String(unit ?? '').trim();
  return u ? `${formatted} ${u}` : formatted;
}

/**
 * Convenience: build formatter options from module_settings + UI locale.
 */
export function formattingContext(moduleSettings, appLocale = 'sk') {
  const ms = moduleSettings || [];
  return {
    locale: appLocale || 'sk',
    dateFormat: getDateFormat(ms) || DEFAULT_DATE_FORMAT,
    timezone: getTimezone(ms),
    currency: getCurrency(ms),
    temperatureUnit: getTemperature(ms),
  };
}

const PAINTING_TEMP_KEYS = ['outdoor_temp', 'indoor_temp'];

function roundTemp(n) {
  return Math.round(n * 100) / 100;
}

/**
 * API/DB stores painting temps in Celsius. Convert record fields to display unit for form edit.
 * Safe on null/invalid; does not mutate input.
 */
export function paintingTempsToDisplay(record, unit = '°C') {
  if (!record || typeof record !== 'object') return record;
  const out = { ...record };
  for (const key of PAINTING_TEMP_KEYS) {
    if (out[key] === '' || out[key] == null) continue;
    const converted = convertTemperature(out[key], '°C', unit);
    if (converted != null) out[key] = roundTemp(converted);
  }
  return out;
}

/**
 * Convert form display-unit temps back to Celsius for API save (exactly once).
 */
export function paintingTempsToStorage(values, unit = '°C') {
  if (!values || typeof values !== 'object') return values;
  const out = { ...values };
  for (const key of PAINTING_TEMP_KEYS) {
    if (out[key] === '' || out[key] == null) continue;
    const converted = convertTemperature(out[key], unit, '°C');
    if (converted != null) out[key] = roundTemp(converted);
  }
  return out;
}
